/**
 * Iter 67el — Vendor inbox panel for AI-chat handoffs.
 *
 * Polls `/api/vendor/chat-handoffs` every 20 s. When the unread count
 * goes UP between polls, plays a soft chime (Web Audio API — no asset
 * download, works offline). Renders the unread badge in the header and
 * a list of pending handoffs with a "Mark read" action.
 *
 * Designed to sit in the Vendor Dashboard alongside booking notifications.
 */
import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Bell, MessageCircle, ExternalLink, Check } from "lucide-react";

const POLL_INTERVAL_MS = 20000;

function _formatRelative(iso) {
  if (!iso) return "—";
  try {
    const diff = Date.now() - new Date(iso).getTime();
    const m = Math.floor(diff / 60000);
    if (m < 1) return "just now";
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.floor(h / 24);
    return `${d}d ago`;
  } catch {
    return iso;
  }
}

/**
 * Plays a soft 2-note chime using the Web Audio API — no external file
 * needed, so it never gets blocked by a slow CDN or a privacy-mode
 * browser. Returns silently if the browser doesn't allow audio yet
 * (autoplay policies — user must have interacted with the page).
 */
function _playChime() {
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    const ctx = new AC();
    const now = ctx.currentTime;
    const notes = [880, 1318.5];  // A5 → E6, friendly upward chime
    notes.forEach((freq, i) => {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = "sine";
      o.frequency.value = freq;
      const start = now + i * 0.18;
      const end = start + 0.28;
      g.gain.setValueAtTime(0.0001, start);
      g.gain.exponentialRampToValueAtTime(0.25, start + 0.04);
      g.gain.exponentialRampToValueAtTime(0.0001, end);
      o.connect(g).connect(ctx.destination);
      o.start(start);
      o.stop(end + 0.02);
    });
    setTimeout(() => ctx.close().catch(() => {}), 1200);
  } catch { /* audio blocked */ }
}

export default function VendorChatHandoffsPanel() {
  const [handoffs, setHandoffs] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const lastUnreadRef = useRef(null);
  const [muted, setMuted] = useState(false);

  const refresh = async () => {
    try {
      const { data } = await api.get("/vendor/chat-handoffs", { params: { days: 7, limit: 30 } });
      setHandoffs(data.handoffs || []);
      const next = data.unread_count || 0;
      // Chime only when the unread count strictly increases — avoid replaying on every poll.
      if (lastUnreadRef.current != null && next > lastUnreadRef.current && !muted) {
        _playChime();
      }
      lastUnreadRef.current = next;
      setUnreadCount(next);
    } catch {
      // Silent — panel disappears if backend is unreachable
    }
  };

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, POLL_INTERVAL_MS);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const markRead = async (id) => {
    try {
      await api.post(`/vendor/chat-handoffs/${id}/mark-read`);
      setHandoffs((prev) => prev.map((h) => (h.id === id ? { ...h, is_read: true } : h)));
      setUnreadCount((c) => Math.max(0, c - 1));
    } catch { /* ignore */ }
  };

  if (handoffs.length === 0) {
    return null;  // hide entirely when there's nothing to act on
  }

  return (
    <div
      className="rounded-2xl border border-emerald-200 bg-gradient-to-br from-emerald-50/60 to-white p-5"
      data-testid="vendor-chat-handoffs"
    >
      <div className="flex items-start gap-3 mb-4">
        <div className="relative h-10 w-10 rounded-xl bg-emerald-100 ring-1 ring-emerald-200 flex items-center justify-center shrink-0">
          <MessageCircle className="h-5 w-5 text-emerald-700" />
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 h-5 min-w-5 px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center" data-testid="vendor-chat-handoffs-badge">
              {unreadCount}
            </span>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-display text-lg font-semibold">Chat handoffs (last 7 days)</div>
          <p className="text-xs text-neutral-600 mt-0.5">
            Renters who clicked "Talk to a human" from the AI chat. Each entry already opened your WhatsApp on the renter's side — follow up there.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setMuted((m) => !m)}
          className="text-[11px] font-semibold text-neutral-500 hover:text-neutral-900 inline-flex items-center gap-1 shrink-0"
          aria-label={muted ? "Unmute notifications" : "Mute notifications"}
          data-testid="vendor-chat-handoffs-mute"
        >
          <Bell className="h-3.5 w-3.5" />
          {muted ? "Muted" : "Chime on"}
        </button>
      </div>

      <div className="space-y-2" data-testid="vendor-chat-handoffs-list">
        {handoffs.map((h) => (
          <div
            key={h.id}
            className={`rounded-xl border bg-white p-3 flex items-start gap-3 ${h.is_read ? "border-neutral-200 opacity-70" : "border-emerald-300 ring-1 ring-emerald-100"}`}
            data-testid={`vendor-chat-handoff-${h.id}`}
          >
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-medium">{_formatRelative(h.created_at)}</span>
                {!h.is_read && (
                  <span className="text-[10px] uppercase tracking-wider font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded-full">
                    New
                  </span>
                )}
                {h.car_id && (
                  <span className="text-[10px] text-neutral-500 font-mono">car {h.car_id.slice(0, 8)}</span>
                )}
              </div>
              {h.summary && (
                <div className="text-xs text-neutral-700 mt-1 italic">"{h.summary}"</div>
              )}
              <div className="text-[11px] text-neutral-500 mt-1 inline-flex items-center gap-1">
                <ExternalLink className="h-3 w-3" /> WhatsApp opened to <code className="font-mono">+{h.phone}</code>
              </div>
            </div>
            {!h.is_read && (
              <button
                type="button"
                onClick={() => markRead(h.id)}
                className="h-8 w-8 rounded-lg hover:bg-emerald-50 text-emerald-700 flex items-center justify-center shrink-0"
                aria-label="Mark read"
                data-testid={`vendor-chat-handoff-mark-read-${h.id}`}
              >
                <Check className="h-4 w-4" />
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
